import { useQuery } from '@apollo/client'
import { Clear } from '@mui/icons-material'
import {
    Alert,
    Box,
    Chip,
    IconButton,
    MenuItem,
    Stack,
    TextField,
} from '@mui/material'
import { DataGridPro, type GridColDef } from '@mui/x-data-grid-pro'
import { type FC } from 'react'
import { codec, useUrlState } from 'shared/hooks/useUrlState'
import { formatRelativeTime } from 'shared/utils'
import { BotAuditFeedbackFilter } from '__generated__/graphql'
import { BotAuditMessages } from './BotAudit.gql'
import { FeedbackPill } from './FeedbackPill'
import { ThreadDrawer } from './ThreadDrawer'
import { VerdictPill } from './VerdictPill'

const PAGE_SIZE = 50
// Page header + tabs + sticky filter bar + outer padding.
const GRID_HEIGHT_OFFSET_PX = 320
const ALL_VERDICTS = ['yes', 'partial', 'no', 'unjudged'] as const
const ACTIONS = ['search', 'detail', 'clarify', 'help'] as const
const VALID_FEEDBACK = Object.values(BotAuditFeedbackFilter)
const FEEDBACK_OPTIONS: Array<{
    value: '' | BotAuditFeedbackFilter
    label: string
}> = [
    { value: '', label: 'Any' },
    { value: BotAuditFeedbackFilter.Positive, label: 'Thumbs up' },
    { value: BotAuditFeedbackFilter.Negative, label: 'Thumbs down' },
    { value: BotAuditFeedbackFilter.Any, label: 'Has feedback' },
    { value: BotAuditFeedbackFilter.None, label: 'No feedback' },
]
const isFeedbackFilterValue = (v: string): v is BotAuditFeedbackFilter =>
    (VALID_FEEDBACK as readonly string[]).includes(v)

// Returns undefined for empty/invalid URL date strings.
const toValidDate = (s: string | undefined): Date | undefined => {
    if (!s) return undefined
    const d = new Date(s)
    return isNaN(d.getTime()) ? undefined : d
}

// Local end-of-day constructor avoids the DST edge case where a day is
// 23 or 25 hours long (adding 86400000ms misses an hour on transitions).
const toEndOfDay = (d: Date | undefined): Date | undefined =>
    d
        ? new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999)
        : undefined

const COLUMNS: GridColDef[] = [
    {
        field: 'createdAt',
        headerName: 'Time',
        width: 110,
        renderCell: ({ value }) => {
            const d = new Date(value)
            return (
                <span title={d.toLocaleString()}>{formatRelativeTime(d)}</span>
            )
        },
    },
    {
        field: 'user',
        headerName: 'User',
        width: 160,
        sortable: false,
        renderCell: ({ row }) => {
            const u = row.user
            if (u)
                return (
                    `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() ||
                    u.email ||
                    row.slackUserId
                )
            return row.slackUserId
        },
    },
    {
        field: 'action',
        headerName: 'Action',
        width: 100,
        renderCell: ({ value }) =>
            value ? (
                <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    {value}
                </Box>
            ) : null,
    },
    {
        field: 'precedingUserText',
        headerName: 'Query',
        flex: 1,
        sortable: false,
        renderCell: ({ value }) => (
            <Box
                sx={{
                    fontSize: '0.8rem',
                    color: 'text.secondary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            >
                {value || <em>—</em>}
            </Box>
        ),
    },
    {
        field: 'text',
        headerName: 'Response',
        flex: 1.5,
        sortable: false,
        renderCell: ({ value }) => (
            <Box
                sx={{
                    fontSize: '0.85rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            >
                {value}
            </Box>
        ),
    },
    {
        field: 'judgeVerdict',
        headerName: 'Verdict',
        width: 100,
        renderCell: ({ value }) => <VerdictPill verdict={value} />,
    },
    {
        field: 'feedback',
        headerName: 'Feedback',
        width: 90,
        align: 'center',
        renderCell: ({ value }) => <FeedbackPill feedback={value} />,
    },
    {
        field: 'durationMs',
        headerName: 'Latency',
        width: 90,
        align: 'right',
        renderCell: ({ value }) => (value != null ? `${value}ms` : '—'),
    },
]

const filterCodec = {
    search: codec.string(),
    action: codec.string(),
    feedback: codec.string(),
    verdicts: codec.stringArray({ defaultValue: [] }),
    startDate: codec.string(),
    endDate: codec.string(),
    page: codec.int({ defaultValue: 1 }),
    conversationId: codec.string(),
}

export const ConversationsTab: FC = () => {
    const { state, setState, isDefault } = useUrlState(filterCodec)

    const feedbackFilter =
        state.feedback && isFeedbackFilterValue(state.feedback)
            ? state.feedback
            : undefined

    const startDate = toValidDate(state.startDate)
    const endDate = toEndOfDay(toValidDate(state.endDate))

    const { data, loading, error } = useQuery(BotAuditMessages, {
        variables: {
            startDate,
            endDate,
            // 'unjudged' is a sentinel the backend maps to WHERE judge_verdict IS NULL.
            verdicts: state.verdicts.length ? state.verdicts : undefined,
            feedback: feedbackFilter,
            action: state.action || undefined,
            search: state.search || undefined,
            page: state.page ?? 1,
            limit: PAGE_SIZE,
        },
        // Audit dashboard - keep what we've cached but always re-fetch in
        // background so verdicts / feedback updates land without a refresh.
        fetchPolicy: 'cache-and-network',
    })

    const total = data?.botAuditMessages.total ?? 0
    const rows = data?.botAuditMessages.rows ?? []

    const toggleVerdict = (v: string) => {
        const next = state.verdicts.includes(v)
            ? state.verdicts.filter((x) => x !== v)
            : [...state.verdicts, v]
        setState({ verdicts: next, page: 1 })
    }

    return (
        <Stack spacing={2}>
            <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{
                    p: 1.5,
                    bgcolor: 'background.paper',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                }}
                data-testid="bot-audit-conversations-filters"
            >
                <TextField
                    size="small"
                    label="Search text"
                    value={state.search ?? ''}
                    onChange={(e) =>
                        setState({ search: e.target.value, page: 1 })
                    }
                    sx={{ minWidth: 220 }}
                    data-testid="bot-audit-filter-search"
                />
                <TextField
                    size="small"
                    select
                    label="Action"
                    value={state.action ?? ''}
                    onChange={(e) =>
                        setState({ action: e.target.value, page: 1 })
                    }
                    sx={{ minWidth: 130 }}
                    data-testid="bot-audit-filter-action"
                >
                    <MenuItem value="">All</MenuItem>
                    {ACTIONS.map((a) => (
                        <MenuItem key={a} value={a}>
                            {a}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    size="small"
                    select
                    label="Feedback"
                    value={state.feedback ?? ''}
                    onChange={(e) =>
                        setState({ feedback: e.target.value, page: 1 })
                    }
                    sx={{ minWidth: 150 }}
                    data-testid="bot-audit-filter-feedback"
                >
                    {FEEDBACK_OPTIONS.map((f) => (
                        <MenuItem key={f.value} value={f.value}>
                            {f.label}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    size="small"
                    type="date"
                    label="From"
                    InputLabelProps={{ shrink: true }}
                    value={state.startDate ?? ''}
                    onChange={(e) =>
                        setState({ startDate: e.target.value, page: 1 })
                    }
                    sx={{ minWidth: 150 }}
                    data-testid="bot-audit-filter-start-date"
                />
                <TextField
                    size="small"
                    type="date"
                    label="To"
                    InputLabelProps={{ shrink: true }}
                    value={state.endDate ?? ''}
                    onChange={(e) =>
                        setState({ endDate: e.target.value, page: 1 })
                    }
                    sx={{ minWidth: 150 }}
                    data-testid="bot-audit-filter-end-date"
                />
                <Stack direction="row" spacing={0.5} alignItems="center">
                    <Box
                        sx={{
                            fontSize: '0.75rem',
                            color: 'text.secondary',
                            mr: 0.5,
                        }}
                    >
                        Verdict:
                    </Box>
                    {ALL_VERDICTS.map((v) => {
                        const active = state.verdicts.includes(v)
                        return (
                            <Chip
                                key={v}
                                label={v}
                                size="small"
                                clickable
                                onClick={() => toggleVerdict(v)}
                                color={active ? 'primary' : 'default'}
                                variant={active ? 'filled' : 'outlined'}
                                sx={{
                                    textTransform: 'capitalize',
                                    fontSize: '0.7rem',
                                    height: 22,
                                }}
                                data-testid={`bot-audit-filter-verdict-${v}`}
                            />
                        )
                    })}
                </Stack>
                {!isDefault ? (
                    <IconButton
                        size="small"
                        onClick={() =>
                            setState({
                                search: undefined,
                                action: undefined,
                                feedback: undefined,
                                verdicts: [],
                                startDate: undefined,
                                endDate: undefined,
                                page: 1,
                            })
                        }
                        title="Clear filters"
                        data-testid="bot-audit-filters-clear"
                    >
                        <Clear fontSize="small" />
                    </IconButton>
                ) : null}
            </Stack>

            {error ? (
                <Alert severity="error">
                    Failed to load messages: {error.message}
                </Alert>
            ) : null}

            <Box
                sx={{
                    height: `calc(100vh - ${GRID_HEIGHT_OFFSET_PX}px)`,
                    minHeight: 400,
                }}
            >
                <DataGridPro
                    rows={rows}
                    columns={COLUMNS}
                    loading={loading}
                    pagination
                    paginationMode="server"
                    rowCount={total}
                    paginationModel={{
                        page: (state.page ?? 1) - 1,
                        pageSize: PAGE_SIZE,
                    }}
                    onPaginationModelChange={(m) =>
                        setState({ page: m.page + 1 })
                    }
                    pageSizeOptions={[PAGE_SIZE]}
                    disableRowSelectionOnClick
                    getRowId={(r) => r.id}
                    density="compact"
                    sx={{ bgcolor: 'background.paper', cursor: 'pointer' }}
                    onRowClick={(params) =>
                        setState({ conversationId: params.row.conversationId })
                    }
                    data-testid="bot-audit-conversations-grid"
                />
            </Box>

            <ThreadDrawer
                conversationId={state.conversationId ?? null}
                onClose={() => setState({ conversationId: undefined })}
            />
        </Stack>
    )
}
